// ===================================================================
// SHELTER — Family Reunification
// Missing person requests, matching to survivors, reunification workflow
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Textarea,
  Modal, Stack, SimpleGrid, Table, ActionIcon, Drawer, Divider, SegmentedControl,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconRefresh, IconUserSearch, IconShare,
  IconPrinter, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useNavigate } from 'react-router-dom';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatPhone } from '../../lib/phoneFormat';
import {
  scoreFullMatch, ageFromDob, confColor,
  type QueryRec,
} from '../../lib/reunificationMatch';
import type { ReunificationRequest, ReunificationStatus } from '../../types/shelter';

const STATUS_COLORS: Record<ReunificationStatus, string> = {
  searching: 'yellow', matched: 'blue', reunified: 'green', closed: 'gray',
};

const STATUS_OPTIONS: { value: ReunificationStatus; label: string }[] = [
  { value: 'searching', label: 'Searching' },
  { value: 'matched', label: 'Matched' },
  { value: 'reunified', label: 'Reunified' },
  { value: 'closed', label: 'Closed' },
];

const EMPTY: Partial<ReunificationRequest> = {
  requestedByName: '', requestedByPhone: '', requestedByEmail: '',
  missingPersonName: '', missingPersonDescription: '',
  missingPersonDob: '', missingPersonPhone: '', missingPersonAge: '',
  lastKnownLocation: '', lastKnownDate: '', relationship: '',
  status: 'searching', notes: '', source: '',
};


export default function Reunification() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    reunification, survivors, loadingReunification,
    loadReunification, loadSurvivors,
    createReunification, updateReunification,
  } = useShelterStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ReunificationRequest | null>(null);
  const [form, setForm] = useState<Partial<ReunificationRequest>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ReunificationRequest | null>(null);
  const [matchSearch, setMatchSearch] = useState('');

  useEffect(() => { loadReunification(); loadSurvivors(); }, [incidentId, loadReunification, loadSurvivors]);

  // Store already server-side filters by active incident; use directly
  const iRequests = incidentId ? reunification : [];
  const iSurvivors = incidentId ? survivors : [];

  const survivorName = (id: string) => {
    const s = survivors.find(sv => String(sv.id) === id);
    return s ? `${s.firstName} ${s.lastName}` : id;
  };

  const matchResults = useMemo(() => {
    // Use the form's full info (name from search box if typed, plus dob/phone/age from form)
    const queryName = matchSearch.trim() || form.missingPersonName || '';
    if (!queryName && !form.missingPersonDob && !form.missingPersonPhone) return [];
    const q: QueryRec = {
      name: queryName,
      dob: form.missingPersonDob,
      phone: form.missingPersonPhone,
      age: form.missingPersonAge,
    };
    const scored = iSurvivors.map(s => {
      const result = scoreFullMatch(q, s);
      return result ? { s, ...result } : null;
    }).filter(Boolean) as { s: typeof iSurvivors[0]; confidence: number; reasons: string[] }[];
    return scored.sort((a, b) => b.confidence - a.confidence).slice(0, 10);
  }, [iSurvivors, matchSearch, form.missingPersonName, form.missingPersonDob, form.missingPersonPhone, form.missingPersonAge]);

  // Best fuzzy match per request — for table badges
  const bestMatchByRequest = useMemo(() => {
    const map = new Map<number, { s: typeof iSurvivors[0]; confidence: number; reasons: string[] }>();
    iRequests.forEach(r => {
      if (!r.missingPersonName && !r.missingPersonDob && !r.missingPersonPhone) return;
      const q: QueryRec = {
        name: r.missingPersonName,
        dob: r.missingPersonDob,
        phone: r.missingPersonPhone,
        age: r.missingPersonAge,
      };
      let best: { s: typeof iSurvivors[0]; confidence: number; reasons: string[] } | null = null;
      for (const s of iSurvivors) {
        const res = scoreFullMatch(q, s);
        if (res && (!best || res.confidence > best.confidence)) {
          best = { s, confidence: res.confidence, reasons: res.reasons };
        }
      }
      if (best) map.set(r.id, best);
    });
    return map;
  }, [iRequests, iSurvivors]);

  const filtered = useMemo(() => {
    let list = iRequests;
    if (statusFilter && statusFilter !== 'all') list = list.filter(r => r.status === statusFilter);
    if (sourceFilter === 'public') list = list.filter(r => r.source === 'public');
    else if (sourceFilter === 'staff') list = list.filter(r => r.source !== 'public');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.missingPersonName.toLowerCase().includes(q) ||
        r.requestedByName.toLowerCase().includes(q) ||
        r.relationship.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (b.id || 0) - (a.id || 0);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iRequests, statusFilter, sourceFilter, search, sortField, sortDir]);

  const publicCount = useMemo(() => iRequests.filter(r => r.source === 'public').length, [iRequests]);

  const setField = (key: keyof ReunificationRequest, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setMatchSearch('');
    setModalOpen(true);
  };

  const openEdit = (r: ReunificationRequest) => {
    setEditTarget(r);
    setForm({ ...r });
    setMatchSearch('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.missingPersonName?.trim()) {
      notifications.show({ message: 'Missing person name is required', color: 'yellow' });
      return;
    }
    if (!form.requestedByName?.trim()) {
      notifications.show({ message: 'Requester name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        createdAt: editTarget ? form.createdAt : new Date().toISOString(),
      };

      if (form.status === 'reunified' && !form.resolvedAt) {
        payload.resolvedAt = new Date().toISOString();
      }
      if (form.status === 'closed' && !form.resolvedAt) {
        payload.resolvedAt = new Date().toISOString();
      }

      if (editTarget) {
        await updateReunification(editTarget.id, payload);
        notifications.show({ message: 'Request updated', color: 'green' });
      } else {
        await createReunification(payload);
        notifications.show({ message: 'Reunification request created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleMatch = (survivorId: string) => {
    setForm(prev => ({
      ...prev,
      matchedSurvivorId: String(survivorId),
      status: 'matched' as ReunificationStatus,
    }));
    setMatchSearch('');
    notifications.show({ message: `Matched to ${survivorName(String(survivorId))}`, color: 'blue' });
  };

  const exportCSV = () => {
    const escape = (v: string) => `"${(v || '').replace(/"/g, '""')}"`;
    const headers = ['Missing Person', 'DOB', 'Age', 'Phone', 'Description', 'Last Known Location', 'Last Known Date', 'Relationship', 'Requested By', 'Requester Phone', 'Requester Email', 'Status', 'Source', 'Matched Survivor', 'Created'];
    const rows = filtered.map(r => [
      r.missingPersonName,
      r.missingPersonDob || '',
      r.missingPersonAge || '',
      r.missingPersonPhone || '',
      r.missingPersonDescription || '',
      r.lastKnownLocation || '',
      r.lastKnownDate || '',
      r.relationship || '',
      r.requestedByName || '',
      r.requestedByPhone || '',
      r.requestedByEmail || '',
      r.status,
      r.source || 'staff',
      r.matchedSurvivorId ? survivorName(r.matchedSurvivorId) : '',
      r.createdAt || '',
    ].map(escape).join(','));
    const csv = headers.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reunification-requests-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notifications.show({ message: 'CSV exported', color: 'green' });
  };

  const handlePrint = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const rowsHtml = filtered.map(r => `
      <tr>
        <td>${r.missingPersonName}${r.source === 'public' ? ' <span class="badge">PUBLIC</span>' : ''}</td>
        <td>${r.missingPersonDob || '-'}</td>
        <td>${r.missingPersonAge || '-'}</td>
        <td>${r.missingPersonPhone || '-'}</td>
        <td>${r.requestedByName || '-'}</td>
        <td>${r.requestedByPhone || '-'}</td>
        <td>${r.relationship || '-'}</td>
        <td>${r.lastKnownLocation || '-'}</td>
        <td>${r.status}</td>
        <td>${r.matchedSurvivorId ? survivorName(r.matchedSurvivorId) : '-'}</td>
        <td>${r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '-'}</td>
      </tr>
    `).join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`
      <html><head><title>Reunification Requests</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;font-size:11px}
        table{width:100%;border-collapse:collapse;margin:12px 0}
        th,td{border:1px solid #ccc;padding:5px 7px;text-align:left;vertical-align:top}
        th{background:#f5f5f5;font-weight:700}
        h1{font-size:18px;margin:0}
        .header{border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:16px}
        .badge{background:#7c3aed;color:#fff;padding:1px 5px;border-radius:3px;font-size:9px}
        @media print{body{padding:0}}
      </style>
      </head><body>
        <div class="header">
          <h1>OpenGov EMC — Family Reunification Requests</h1>
          <div>Incident: ${activeIncident?.name ?? 'All'} &middot; Generated: ${today} &middot; ${filtered.length} request${filtered.length !== 1 ? 's' : ''}</div>
        </div>
        <table>
          <thead><tr>
            <th>Missing Person</th><th>DOB</th><th>Age</th><th>Phone</th>
            <th>Requested By</th><th>Requester Phone</th><th>Relationship</th>
            <th>Last Known Location</th><th>Status</th><th>Matched Survivor</th><th>Created</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body></html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Family Reunification</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingReunification} onClick={() => loadReunification()}>Refresh</Button>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>Export CSV</Button>
          <Button size="xs" variant="light" leftSection={<IconPrinter size={14} />} onClick={handlePrint}>Print</Button>
          <Button size="xs" variant="light" leftSection={<IconShare size={14} />} onClick={() => {
            const url = `${window.location.origin}/shelter/find-person${incidentId ? `?incident=${incidentId}` : ''}`;
            navigator.clipboard.writeText(url);
            notifications.show({ message: 'Public form link copied to clipboard', color: 'blue' });
          }}>Share Public Form</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Request</Button>}
        </Group>
      </Group>

      <Group gap="xs" mb="md">
        <TextInput size="xs" placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <SegmentedControl size="xs" value={sourceFilter} onChange={setSourceFilter}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Staff', value: 'staff' },
            { label: `Public${publicCount > 0 ? ` (${publicCount})` : ''}`, value: 'public' },
          ]} />
        <Select size="xs" placeholder="Status" data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter} onChange={setStatusFilter} clearable w={160} />
      </Group>

      <Paper withBorder radius="md" style={{ maxHeight: 500, overflow: 'auto' }}>
        <Table striped highlightOnHover stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('missingPersonName')}>Missing Person{sortIcon('missingPersonName')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('requestedByName')}>Requested By{sortIcon('requestedByName')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('relationship')}>Relationship{sortIcon('relationship')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
              <Table.Th>Matched Survivor</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('createdAt')}>Date{sortIcon('createdAt')}</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr><Table.Td colSpan={7}><Text c="dimmed" ta="center" py="lg">No requests</Text></Table.Td></Table.Tr>
            )}
            {filtered.map(r => (
              <Table.Tr key={r.id} onClick={() => setDetailTarget(r)} style={{ cursor: 'pointer' }}>
                <Table.Td fw={500}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500}>{r.missingPersonName}</Text>
                    {r.source === 'public' && (
                      <Badge size="xs" color="violet" variant="light" title="Submitted via public form">Public</Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{r.requestedByName}</Table.Td>
                <Table.Td>{r.relationship || '-'}</Table.Td>
                <Table.Td>
                  <Badge size="xs" color={STATUS_COLORS[r.status]}>{(r.status || '').replace(/_/g, ' ')}</Badge>
                </Table.Td>
                <Table.Td>
                  {r.matchedSurvivorId ? (
                    (() => {
                      const matched = survivors.find(sv => String(sv.id) === r.matchedSurvivorId);
                      const conf = matched ? scoreFullMatch(
                        { name: r.missingPersonName, dob: r.missingPersonDob, phone: r.missingPersonPhone, age: r.missingPersonAge },
                        matched,
                      ) : null;
                      return (
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">{survivorName(r.matchedSurvivorId)}</Text>
                          {conf && (
                            <Badge size="xs" color={confColor(conf.confidence)} variant="filled"
                              title={`Matched via ${conf.reasons.join(' + ')}`}>
                              {conf.confidence}%
                            </Badge>
                          )}
                        </Group>
                      );
                    })()
                  ) : (() => {
                    const sug = bestMatchByRequest.get(r.id);
                    return sug ? (
                      <Group gap={6} wrap="nowrap">
                        <Text size="xs" c="dimmed">Suggest:</Text>
                        <Text size="xs">{sug.s.firstName} {sug.s.lastName}</Text>
                        <Badge size="xs" color={confColor(sug.confidence)} variant="light"
                          title={`Best candidate via ${sug.reasons.join(' + ')}`}>
                          {sug.confidence}%
                        </Badge>
                      </Group>
                    ) : <Text size="sm" c="dimmed">-</Text>;
                  })()}
                </Table.Td>
                <Table.Td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '-'}</Table.Td>
                {canEdit && (
                <Table.Td onClick={e => e.stopPropagation()}>
                  <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(r)}>
                    <IconPencil size={14} />
                  </ActionIcon>
                </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Text size="xs" c="dimmed" mt="xs">{filtered.length} request{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} position="right" size="md"
        title={<Text fw={700} size="lg">Reunification Details</Text>}>
        {detailTarget && (
          <Stack gap="md">
            <Group gap="xs">
              <Badge size="lg" color={STATUS_COLORS[detailTarget.status]}>{(detailTarget.status || '').replace(/_/g, ' ')}</Badge>
              {detailTarget.source === 'public' && (
                <Badge size="lg" color="violet" variant="light">Public Submission</Badge>
              )}
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Missing Person</Text>
              <Text size="lg" fw={700}>{detailTarget.missingPersonName}</Text>
            </div>

            {detailTarget.missingPersonDescription && (
              <div>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{detailTarget.missingPersonDescription}</Text>
              </div>
            )}

            <SimpleGrid cols={3}>
              <div>
                <Text size="xs" c="dimmed">DOB</Text>
                <Text size="sm">{detailTarget.missingPersonDob ? detailTarget.missingPersonDob.slice(0, 10) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Age</Text>
                <Text size="sm">{detailTarget.missingPersonAge || (detailTarget.missingPersonDob ? String(ageFromDob(detailTarget.missingPersonDob) ?? '-') : '-')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Phone</Text>
                <Text size="sm">{detailTarget.missingPersonPhone ? formatPhone(detailTarget.missingPersonPhone) : '-'}</Text>
              </div>
            </SimpleGrid>

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Last Known Location</Text>
                <Text size="sm">{detailTarget.lastKnownLocation || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Last Known Date</Text>
                <Text size="sm">{detailTarget.lastKnownDate ? new Date(detailTarget.lastKnownDate).toLocaleDateString() : '-'}</Text>
              </div>
            </SimpleGrid>

            <Divider label="Requested By" labelPosition="left" />

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Name</Text>
                <Text size="sm">{detailTarget.requestedByName || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Phone</Text>
                <Text size="sm">{detailTarget.requestedByPhone ? formatPhone(detailTarget.requestedByPhone) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Email</Text>
                <Text size="sm">{detailTarget.requestedByEmail || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Relationship</Text>
                <Text size="sm">{detailTarget.relationship || '-'}</Text>
              </div>
            </SimpleGrid>

            <Divider />

            {detailTarget.matchedSurvivorId && (
              <div>
                <Text size="xs" c="dimmed">Matched Survivor</Text>
                <Group gap="xs">
                  <Text size="sm" fw={500}>{survivorName(detailTarget.matchedSurvivorId)}</Text>
                  <Button size="compact-xs" variant="subtle" onClick={() => { setDetailTarget(null); navigate(`/shelter/survivor/${detailTarget.matchedSurvivorId}`); }}>
                    View Survivor
                  </Button>
                </Group>
              </div>
            )}

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Created At</Text>
                <Text size="sm">{detailTarget.createdAt ? new Date(detailTarget.createdAt).toLocaleString() : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Resolved At</Text>
                <Text size="sm">{detailTarget.resolvedAt ? new Date(detailTarget.resolvedAt).toLocaleString() : '-'}</Text>
              </div>
            </SimpleGrid>

            {detailTarget.notes && (
              <div>
                <Text size="xs" c="dimmed">Notes</Text>
                <Text size="sm">{detailTarget.notes}</Text>
              </div>
            )}

            <Divider />

            {canEdit && (
            <Group justify="flex-end">
              <Button variant="light" leftSection={<IconPencil size={14} />}
                onClick={() => { const t = detailTarget; setDetailTarget(null); openEdit(t); }}>
                Edit
              </Button>
            </Group>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Reunification Request' : 'New Reunification Request'} size="lg">
        <Stack gap="sm">
          <Text fw={700} size="sm">Missing Person</Text>
          <SimpleGrid cols={2}>
            <TextInput label="Missing Person Name" value={form.missingPersonName ?? ''}
              onChange={e => setField('missingPersonName', e.target.value)} required />
            <TextInput label="Relationship" value={form.relationship ?? ''}
              onChange={e => setField('relationship', e.target.value)} />
          </SimpleGrid>
          <SimpleGrid cols={3}>
            <TextInput label="Date of Birth" type="date"
              value={form.missingPersonDob?.split('T')[0] ?? ''}
              onChange={e => setField('missingPersonDob', e.target.value)} />
            <TextInput label="Approximate Age" type="number" placeholder="if DOB unknown"
              value={form.missingPersonAge ?? ''}
              onChange={e => setField('missingPersonAge', e.target.value)} />
            <TextInput label="Phone (missing person)"
              value={form.missingPersonPhone ?? ''}
              onChange={e => setField('missingPersonPhone', formatPhone(e.target.value))} />
          </SimpleGrid>
          <Textarea label="Description" rows={2} value={form.missingPersonDescription ?? ''}
            onChange={e => setField('missingPersonDescription', e.target.value)} />
          <SimpleGrid cols={2}>
            <TextInput label="Last Known Location" value={form.lastKnownLocation ?? ''}
              onChange={e => setField('lastKnownLocation', e.target.value)} />
            <TextInput label="Last Known Date" type="date" value={form.lastKnownDate?.split('T')[0] ?? ''}
              onChange={e => setField('lastKnownDate', e.target.value)} />
          </SimpleGrid>

          <Text fw={700} size="sm" mt="sm">Requester Information</Text>
          <SimpleGrid cols={3}>
            <TextInput label="Name" value={form.requestedByName ?? ''}
              onChange={e => setField('requestedByName', e.target.value)} required />
            <TextInput label="Phone" value={form.requestedByPhone ?? ''}
              onChange={e => setField('requestedByPhone', formatPhone(e.target.value))} />
            <TextInput label="Email" value={form.requestedByEmail ?? ''}
              onChange={e => setField('requestedByEmail', e.target.value)} />
          </SimpleGrid>

          <Text fw={700} size="sm" mt="sm">Match & Status</Text>
          {editTarget && (
            <Select label="Status" data={STATUS_OPTIONS} value={form.status ?? 'searching'}
              onChange={v => setField('status', v ?? 'searching')} />
          )}

          {/* Survivor search for matching */}
          <Paper p="sm" withBorder>
                <Text size="sm" fw={600} mb="xs">Search Survivors to Match</Text>
                <TextInput size="xs" placeholder="Search by name..."
                  leftSection={<IconUserSearch size={14} />}
                  value={matchSearch}
                  onChange={e => setMatchSearch(e.target.value)} />
                {matchResults.length > 0 && (
                  <Stack gap={4} mt="xs">
                    {matchResults.map(({ s, confidence, reasons }) => (
                      <Group key={s.id} justify="space-between" p={4}
                        style={{ borderRadius: 4, cursor: 'pointer', background: form.matchedSurvivorId === String(s.id) ? 'var(--mantine-color-blue-0)' : undefined }}
                        onClick={() => handleMatch(String(s.id))}>
                        <div style={{ flex: 1 }}>
                          <Group gap="xs">
                            <Text size="sm" fw={500}>{s.firstName} {s.lastName}</Text>
                            <Badge size="xs" color={confColor(confidence)} variant="filled">{confidence}% match</Badge>
                            {reasons.length > 0 && (
                              <Text size="xs" c="dimmed">via {reasons.join(' + ')}</Text>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {[s.phone ? formatPhone(s.phone) : null, s.dateOfBirth ? `DOB ${s.dateOfBirth.slice(0,10)}` : null].filter(Boolean).join(' · ') || 'No contact info'}
                          </Text>
                        </div>
                        <Badge size="xs" variant="light">{s.status}</Badge>
                      </Group>
                    ))}
                  </Stack>
                )}
            {form.matchedSurvivorId && (
              <Text size="sm" mt="xs" c="blue" fw={600}>
                Matched: {survivorName(form.matchedSurvivorId)}
              </Text>
            )}
          </Paper>

          <Textarea label="Notes" rows={2} value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editTarget ? 'Update' : 'Create'}</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
